import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { getRoles, getUserDetails, getUsersList, updateUserDetails } from 'src/redux/settings-users/api';
import type {
  RoleData,
  RolesState,
  SettingsUsersState,
  UserDetailsResponse,
  UserListItem,
} from 'src/redux/settings-users/types';

// ---- Users (people) ----

const extraReducers = (builder: ActionReducerMapBuilder<SettingsUsersState>) => {
  builder.addCase(getUsersList().pending, (state) => {
    state.isListLoading = true;
  });
  builder.addCase(getUsersList().fulfilled, (state, action) => {
    state.isListLoading = false;
    const items = (action.payload?.data ?? []) as UserListItem[];
    const ids = items.map((item) => item.account_info.user_id);
    if (Number(action.meta.arg?.page || 0) > 1) {
      // A page can land twice — a re-triggered scroll fetch before the
      // previous one is reflected in state, or a slow response the abort
      // missed by a hair. Without this filter, the same id lands in `ids`
      // twice and the same person renders twice.
      const existingIds = new Set(state.list.ids);
      const newIds = ids.filter((id) => !existingIds.has(id));
      state.list = {
        ids: [...state.list.ids, ...newIds],
        meta: action.payload?.meta ?? state.list.meta,
      };
    } else {
      state.list = { ids, meta: action.payload?.meta ?? state.list.meta };
    }
    items.forEach((item) => {
      const userId = item.account_info.user_id;
      state.source[userId] = { ...state.source[userId], ...item };
    });
  });
  builder.addCase(getUsersList().rejected, (state) => {
    state.isListLoading = false;
  });

  builder.addCase(getUserDetails().pending, (state) => {
    state.isLoading = true;
  });
  builder.addCase(getUserDetails().fulfilled, (state, action) => {
    state.isLoading = false;
    const { _user_id: userId, ...response } = action.payload as UserDetailsResponse & {
      _user_id: string;
    };
    if (userId) {
      state.source[userId] = { ...state.source[userId], ...response };
    }
  });
  builder.addCase(getUserDetails().rejected, (state) => {
    state.isLoading = false;
  });

  builder.addCase(updateUserDetails().pending, (state) => {
    state.isSaving = true;
  });
  builder.addCase(updateUserDetails().fulfilled, (state, action) => {
    state.isSaving = false;
    const { _user_id: userId, ...response } = action.payload as Partial<UserDetailsResponse> & {
      _user_id: string;
    };
    if (userId) {
      state.source[userId] = { ...state.source[userId], ...response };
    }
  });
  builder.addCase(updateUserDetails().rejected, (state) => {
    state.isSaving = false;
  });
};

export default extraReducers;

// ---- Roles (Settings → Users → User roles) ----

export const roleExtraReducers = (builder: ActionReducerMapBuilder<RolesState>) => {
  builder.addCase(getRoles().pending, (state: RolesState) => {
    state.roles.isLoading = true;
  });
  builder.addCase(getRoles().fulfilled, (state: RolesState, action) => {
    if (Number(action.meta.arg.page || 0) > 1) {
      // data will be appending to the previous data.
      const roleListData = [
        ...(state?.roles?.data?.data || []),
        ...(action?.payload?.data?.map((item: RoleData) => item?.role_id) || []),
      ];
      if (action?.payload) {
        state.roles.data = {
          data: roleListData,
          meta: action?.payload?.meta || {},
        };
      }
    } else {
      state.roles.data = {
        data: action?.payload?.data?.map((item: RoleData) => item?.role_id),
        meta: action?.payload?.meta || {},
      };
    }
    const newSource = { ...(state?.roles?.source || {}) };
    action?.payload?.data?.forEach((item: RoleData) => {
      newSource[item?.role_id] = item;
    });
    state.roles.source = newSource;
    state.roles.isLoading = false;
  });
  builder.addCase(getRoles().rejected, (state: RolesState) => {
    state.roles.isLoading = false;
  });
};
